import React from "react";
import { useTranslation } from "react-i18next";
/**
 * Custom hook for type-safe translations with react-i18next
 */
export function useAppTranslation() {
    const { t: i18nT, i18n } = useTranslation("common");
    // Type-safe translation function
    const t = (key, options) => {
        return i18nT(key, options);
    };
    // Wrapper for changeLanguage to match expected signature
    const changeLanguage = async (lng) => {
        await i18n.changeLanguage(lng);
    };
    return {
        t,
        changeLanguage,
        currentLanguage: i18n.language,
        isReady: i18n.isInitialized,
    };
}
/**
 * Higher-order component for providing translations to class components
 */
export function withTranslation(Component) {
    return function TranslatedComponent(props) {
        const { t } = useAppTranslation();
        return React.createElement(Component, { ...props, t });
    };
}
/**
 * Utility function to get translations outside of React components
 * NOTE: This only works in Node.js environments, not in the browser
 *
 * @example
 * ```ts
 * import { getTranslation } from '@amzn/awsmp-max-translations';
 *
 * const errorMessage = getTranslation('errorNetwork');
 * console.log(errorMessage); // "Network error occurred"
 * ```
 */
export function getTranslation(key, options) {
    // This requires i18n to be initialized
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const i18n = require("i18next").default;
    return i18n.t(`common:${key}`, options);
}
/**
 * Helper to extract a translation function that can be used outside React context
 * Use this with useAppTranslation to get translations that work in both UI and PDF contexts
 *
 * @example
 * ```tsx
 * const { t } = useAppTranslation();
 * const staticTranslations = extractTranslations(t, ['key1', 'key2']);
 * // Returns { key1: "translated value 1", key2: "translated value 2" }
 * ```
 */
export function extractTranslations(t, keys) {
    const result = {};
    for (const key of keys) {
        result[key] = t(key);
    }
    return result;
}
